import {
  ValveKey,
  ValveRecoveryPolicy,
  ValveState,
} from './device'

// Types for GET /devices/{dUuid}/valve_status — a read-only diagnostic snapshot, distinct
// from device_status.valveStateExtended (device.ts). Same {p,s,c,t} shape per valve, but a
// different endpoint/contract with its own sentinel values, so these are kept as separate
// types rather than reused — see ValveStatusPosition below.

// `p` on this endpoint. 9, not 3, is this endpoint's "could not determine" sentinel — do
// not confuse with ValvePosition (device.ts), which is a different contract.
export type ValveStatusPosition = 0 | 1 | 2 | 9

// `c` on this endpoint. 0–3 only; this endpoint has no backfill sentinel equivalent to
// ValveCommandCode's 9.
export type ValveStatusCommand = 0 | 1 | 2 | 3

// One valve's commissioning result — final status plus the raw open/close phase results
// and travel times, so a fault visible in only one direction isn't hidden by `s`.
export interface ValveCommDataAPI {
  s: ValveState
  o: ValveState
  c: ValveState
  ot: number
  ct: number
}

// One valve's live extended state — same fields as device_status.valveStateExtended, but
// from this endpoint's own contract (see ValveStatusPosition).
export interface ValveStatusExtendedAPI {
  p: ValveStatusPosition
  s: ValveState
  c: ValveStatusCommand
  t: number
}

export type ValveRecoverySource = 'boot' | 'dc_restore' | 'probe'

export type ValveRecoveryOutcome =
  | 'CONFIRMED'
  | 'FOUND'
  | 'RESTORED'
  | 'FAILSAFE_CLOSED'
  | 'ADOPTED'
  | 'UNKNOWN'
  | 'NOT_FITTED'

// One valve's outcome from a recovery sweep — the {p,s,c} triple plus what the sweep
// concluded.
export interface ValveRecoveryDataAPI {
  p: ValveStatusPosition
  s: ValveState
  c: ValveStatusCommand
  r: ValveRecoveryOutcome
}

// The most recent recovery sweep — a discrete event record, not current state. `results`
// is sparse: only valves the sweep actually touched appear (dc_restore sweeps only touch
// valves whose position was unknown). `epoch` is the device RTC clock, which can be 0 or
// wrong if the RTC was never set.
export interface ValveRecoveryLastAPI {
  src: ValveRecoverySource
  epoch: number
  policy: ValveRecoveryPolicy
  results: Partial<Record<ValveKey, ValveRecoveryDataAPI | null>>
}

export type ValveCommSlotsAPI = {
  [Key in ValveKey as `${Lowercase<Key>}Com`]: ValveCommDataAPI | null
}

export type ValveStatusExtendedSlotsAPI = {
  [Key in ValveKey as `${Lowercase<Key>}Extended`]: ValveStatusExtendedAPI | null
}

export interface ValveStatusAPI extends ValveCommSlotsAPI, ValveStatusExtendedSlotsAPI {
  valveRecoveryLast: ValveRecoveryLastAPI | null
}

export const valveCommSlotKey = (key: ValveKey) =>
  `${key.toLowerCase()}Com` as keyof ValveCommSlotsAPI

export const valveStatusExtendedSlotKey = (key: ValveKey) =>
  `${key.toLowerCase()}Extended` as keyof ValveStatusExtendedSlotsAPI

// V6 is never fitted and this endpoint doesn't report it — same V1–V5 convention as the
// rest of the valve feature.
export const VALVE_STATUS_KEYS: ValveKey[] = ['V1', 'V2', 'V3', 'V4', 'V5']
